import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import streamService from "../services/streamService";
import "./StreamSettingsDrawer.css";

const defaultUrlParts = {
  protocol: "rtsp",
  username: "admin",
  password: "",
  ip: "",
  port: "554",
  path: "/Streaming/Channels/101",
};

const emptyForm = {
  id: "",
  ...defaultUrlParts,
  on_demand: false,
  disable_audio: false,
  detection_enabled: true,
};

const parseRtspUrl = (url) => {
  if (!url) return { ...defaultUrlParts };
  try {
    const u = new URL(url);
    return {
      protocol: u.protocol.replace(":", ""),
      username: u.username || "",
      password: u.password || "",
      ip: u.hostname || "",
      port: u.port || (u.protocol === "rtsp:" ? "554" : "554"),
      path: u.pathname + u.search,
    };
  } catch (e) {
    return { ...defaultUrlParts, path: url || "" };
  }
};

const buildRtspUrl = (parts) => {
  const { protocol, username, password, ip, port, path } = parts;
  if (!ip.trim()) return "";
  const auth = username && password ? `${encodeURIComponent(username)}:${encodeURIComponent(password)}@` : username ? `${encodeURIComponent(username)}@` : "";
  const portPart = port && port !== "554" ? `:${port}` : "";
  const safePath = path.startsWith("/") ? path : `/${path}`;
  return `${protocol}://${auth}${ip.trim()}${portPart}${safePath}`;
};

const StreamSettingsDrawer = ({ open, stream, existingStreams = [], onClose, onSaved }) => {
  const isCreate = !stream;
  const title = isCreate ? "Add Camera" : `${stream.name || "Camera"} Settings`;

  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [discovered, setDiscovered] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (stream) {
      const urlParts = parseRtspUrl(stream.url);
      setForm({
        id: stream.id || "",
        ...urlParts,
        on_demand: stream.onDemand || false,
        disable_audio: stream.disableAudio || false,
        detection_enabled: stream.detectionEnabled !== false,
      });
    } else {
      setForm({ ...emptyForm });
    }
    setError(null);
    setSuccess(false);
    setDiscovered([]);
  }, [stream, open]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSuccess(false);
  };

  const handleScan = async () => {
    setScanning(true);
    setError(null);
    setDiscovered([]);
    try {
      const result = await streamService.discoverCameras(1000);
      const candidates = (result?.cameras || []).filter((c) => c.likely_rtsp || c.ports?.some((p) => p.open && [554, 8554, 80, 8000, 37777].includes(p.port)));
      setDiscovered(candidates);
      if (candidates.length === 0) {
        setError("No cameras found on the local network");
      }
    } catch (err) {
      setError(err.message || "Network scan failed");
    } finally {
      setScanning(false);
    }
  };

  const handlePickIp = (ip) => {
    handleChange("ip", ip);
    setDiscovered([]);
  };

  const existingIps = new Set();
  existingStreams.forEach((s) => {
    const parts = parseRtspUrl(s.url);
    if (parts.ip) existingIps.add(parts.ip);
  });

  const getExistingStreamId = (ip) => {
    const found = existingStreams.find((s) => {
      const parts = parseRtspUrl(s.url);
      return parts.ip === ip;
    });
    return found ? found.id : null;
  };

  const handleSave = async () => {
    const url = buildRtspUrl(form);
    if (!url) {
      setError("A valid IP/hostname is required to build the RTSP URL");
      return;
    }
    if (isCreate && !form.id.trim()) {
      setError("Stream ID is required");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const payload = {
        url,
        on_demand: form.on_demand,
        disable_audio: form.disable_audio,
        detection_enabled: form.detection_enabled,
      };
      if (isCreate) {
        await streamService.createStream({ id: form.id.trim(), ...payload });
        setSuccess(true);
        if (onSaved) onSaved();
        onClose();
      } else {
        await streamService.updateStream(stream.id, payload);
        setSuccess(true);
        if (onSaved) onSaved();
      }
    } catch (err) {
      setError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isCreate || !stream) return;
    if (!window.confirm(`Delete camera "${stream.name}"? This cannot be undone.`)) return;
    setSaving(true);
    setError(null);
    try {
      await streamService.deleteStream(stream.id);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to delete stream");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="stream-settings-overlay" onClick={onClose}>
      <div className="stream-settings-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="stream-settings-header">
          <h3>{title}</h3>
          <button type="button" className="stream-settings-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="stream-settings-body">
          <div className="stream-settings-field">
            <label>Stream ID</label>
            <input
              type="text"
              value={form.id}
              onChange={(e) => handleChange("id", e.target.value)}
              disabled={!isCreate}
              className="stream-settings-input"
              placeholder={isCreate ? "e.g. camera_3" : ""}
            />
          </div>

          <div className="stream-settings-field">
            <div className="stream-settings-row">
              <label className="stream-settings-label">Connection</label>
              <button
                type="button"
                className="stream-settings-scan-btn"
                onClick={handleScan}
                disabled={scanning}
              >
                {scanning ? "Scanning..." : "Scan network"}
              </button>
            </div>

            {discovered.length > 0 && (
              <div className="stream-settings-discovered">
                {discovered.map((cam) => {
                  const isExisting = existingIps.has(cam.ip);
                  return (
                    <button
                      key={cam.ip}
                      type="button"
                      className={`stream-settings-discovered-item ${isExisting ? "existing" : ""}`}
                      onClick={() => !isExisting && handlePickIp(cam.ip)}
                      disabled={isExisting}
                    >
                      <span className="stream-settings-discovered-main">
                        <span className="stream-settings-discovered-ip">{cam.ip}</span>
                        {isExisting ? (
                          <span className="stream-settings-discovered-existing">Already added · {getExistingStreamId(cam.ip)}</span>
                        ) : (
                          cam.vendor && (
                            <span className="stream-settings-discovered-vendor">
                              {cam.model ? `${cam.vendor} · ${cam.model}` : cam.vendor}
                            </span>
                          )
                        )}
                      </span>
                      {!isExisting && (
                        <span className="stream-settings-discovered-services">
                          {cam.ports
                            .filter((p) => p.open)
                            .map((p) => p.service)
                            .filter((value, index, self) => self.indexOf(value) === index)
                            .join(", ")}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="stream-settings-url-grid">
              <input
                type="text"
                value={form.username}
                onChange={(e) => handleChange("username", e.target.value)}
                className="stream-settings-input"
                placeholder="Username"
                title="Username"
              />
              <input
                type="password"
                value={form.password}
                onChange={(e) => handleChange("password", e.target.value)}
                className="stream-settings-input"
                placeholder="Password"
                title="Password"
              />
              <input
                type="text"
                value={form.ip}
                onChange={(e) => handleChange("ip", e.target.value)}
                className="stream-settings-input stream-settings-ip"
                placeholder="IP address"
                title="IP address / hostname"
              />
              <input
                type="text"
                value={form.port}
                onChange={(e) => handleChange("port", e.target.value)}
                className="stream-settings-input stream-settings-port"
                placeholder="Port"
                title="Port"
              />
              <input
                type="text"
                value={form.path}
                onChange={(e) => handleChange("path", e.target.value)}
                className="stream-settings-input stream-settings-path"
                placeholder="Path"
                title="RTSP path"
              />
            </div>
            <div className="stream-settings-preview">{buildRtspUrl(form) || "rtsp://..."}</div>
          </div>

          <div className="stream-settings-field stream-settings-toggle-row">
            <label>On Demand</label>
            <button
              type="button"
              className={`stream-settings-toggle ${form.on_demand ? "on" : "off"}`}
              onClick={() => handleChange("on_demand", !form.on_demand)}
            >
              {form.on_demand ? "ON" : "OFF"}
            </button>
          </div>

          <div className="stream-settings-field stream-settings-toggle-row">
            <label>Disable Audio</label>
            <button
              type="button"
              className={`stream-settings-toggle ${form.disable_audio ? "on" : "off"}`}
              onClick={() => handleChange("disable_audio", !form.disable_audio)}
            >
              {form.disable_audio ? "ON" : "OFF"}
            </button>
          </div>

          <div className="stream-settings-field stream-settings-toggle-row">
            <label>Detection Enabled</label>
            <button
              type="button"
              className={`stream-settings-toggle ${form.detection_enabled ? "on" : "off"}`}
              onClick={() => handleChange("detection_enabled", !form.detection_enabled)}
            >
              {form.detection_enabled ? "ON" : "OFF"}
            </button>
          </div>

          {error && <div className="stream-settings-error">{error}</div>}
          {success && <div className="stream-settings-success">{isCreate ? "Camera added!" : "Settings saved!"}</div>}
        </div>

        <div className="stream-settings-footer">
          {!isCreate && (
            <button
              type="button"
              className="stream-settings-delete-btn"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Camera
            </button>
          )}
          <button
            type="button"
            className="stream-settings-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : isCreate ? "Add Camera" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StreamSettingsDrawer;
